// src\themes\lino\elements\Header\SecondaryDrawer.tsx
'use client';

import { ChevronLeft } from 'lucide-react';

import Image from 'next/image';
import Link from 'next/link';

import { Button } from '@/components/ui/button';
import { MenuItem } from '@/core/types/menuApi';
import { useDrawerStore } from '@/stores/drawerStore';

type SidebarDetailsProps = {
  selectedItem?: MenuItem;
};

export default function SecondaryDrawer({ selectedItem }: SidebarDetailsProps) {
  const { primaryOpen, secondaryOpen, setSecondaryOpen, setPrimaryOpen } =
    useDrawerStore();

  const sortedChildren = [...(selectedItem?.children ?? [])].sort((a, b) => {
    if (a.imageUrl && !b.imageUrl) return 1;
    if (!a.imageUrl && b.imageUrl) return -1;
    return 0;
  });

  function handleClose() {
    setSecondaryOpen(false);
    setPrimaryOpen(false);
  }

  return (
    <div
      className={`
    transition-all no-scrollbar duration-300 ease-in-out bg-primary-0 overflow-y-auto
    fixed top-0 h-full z-60
    w-full lg:w-80 
    ltr:left-0 rtl:right-0 
    lg:ltr:left-80 lg:rtl:right-80
    ltr:border-r rtl:border-l border-primary-200
    ${primaryOpen && secondaryOpen
          ? 'translate-x-0 opacity-100 pointer-events-auto'
          : 'ltr:-translate-x-full rtl:translate-x-full opacity-0 pointer-events-none'
        }
  `}
      role="navigation"
      aria-label="Secondary navigation drawer"
    >
      <div className="p-6 min-h-full">
        {selectedItem ? (
          <>
            <div className="flex items-center gap-2 mb-8 pb-4 border-b border-primary-100">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSecondaryOpen(false)}
                aria-label="Back to primary drawer"
                className="rounded-full hover:bg-primary-100 text-primary-900"
              >
                <ChevronLeft className="w-4 h-4 stroke-[1.5] rtl:rotate-180" />
              </Button>
              <span className="text-[13px] tracking-wide text-primary-800">
                {selectedItem.title}
              </span>
            </div>
            <ul className="flex flex-col gap-y-1">
              {sortedChildren.map(child => (
                <li key={child.id} onClick={handleClose}>
                  <Link
                    href={child.link}
                    className="flex items-center gap-x-3 gap-y-3 hover:bg-primary-50 focus:bg-primary-50 rounded-sm p-2"
                  >
                    {child.imageUrl && (
                      <Image
                        src={child.imageUrl}
                        alt={child.imageName ?? 'Untitled image'}
                        height={150}
                        width={200}
                        className="w-20 h-12 rounded-sm object-contain bg-primary-50 p-1"
                      />
                    )}
                    <span className="text-[13px] text-primary-800">
                      {child.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </div>
  );
}
